import { Component, OnInit, Optional } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  CustomerSubscriptionsService,
  CreateCheckoutSessionRequest,
  UpdateSubscriptionRequest,
} from 'ldt-billing-service-api';
import { AuthService } from 'src/app/auth/service/auth.service';
import { Log } from 'src/app/logger.service';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { environment } from 'src/environments/environment';
import { BillingService } from 'src/app/core/services/billing.service';
import { BaseProductSubscriptionComponent } from '../base-product-subscription.component';
import { Observable } from 'rxjs';
import { PlanChangeConfirmDialogComponent } from './plan-change-confirm-dialog.component';

type ChatTierKey = 'free' | 'pro' | 'team' | 'enterprise';

// Relative tier ordering, for deciding whether a change is an upgrade.
const TIER_RANK: Record<ChatTierKey, number> = { free: 0, pro: 1, team: 2, enterprise: 3 };

interface ChatTier {
  key: ChatTierKey;
  eyebrow: string;
  name: string;
  priceDisplay: string;
  priceUnit?: string;
  priceCaption?: string;
  // Stripe price id for self-serve checkout. null for Free (baseline) and
  // Enterprise (contact sales).
  priceId: string | null;
  highlighted?: boolean;
}

// One row of the comparison grid: a feature with its value in each tier column.
// To grow the grid, add a row here (carryover credits, deep-dive / MCP add-ons,
// etc.) — use comingSoon to render it muted/"soon" while it's pre-launch.
interface ChatFeatureRow {
  label: string;
  values: Record<ChatTierKey, string>;
  comingSoon?: boolean;
}

/**
 * Workforce.ai (chat) subscription card. A tiered pricing table
 * (Free / Pro / Team / Enterprise) — flat plans, no seat selector.
 */
@Component({
  selector: 'app-billing-products-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  standalone: false,
})
export class ChatProductSubscriptionComponent
  extends BaseProductSubscriptionComponent
  implements OnInit
{
  currentPriceId: string | null = null;
  isOrgAdmin$: Observable<boolean>;
  isDialog = false;

  readonly tiers: ChatTier[] = [
    {
      key: 'free',
      eyebrow: 'CURIOUS',
      name: 'Free',
      priceDisplay: '$0',
      priceUnit: '/mo',
      priceId: null,
    },
    {
      key: 'pro',
      eyebrow: 'ANALYST',
      name: 'Pro',
      priceDisplay: '$49',
      priceUnit: '/mo',
      priceCaption: '$39/mo billed annually',
      priceId: environment.chatProSubscriptionPriceId,
      highlighted: true,
    },
    {
      key: 'team',
      eyebrow: 'DESK',
      name: 'Team',
      priceDisplay: '$299',
      priceUnit: '/mo',
      priceCaption: '5 seats, +$59/seat',
      priceId: environment.chatTeamSubscriptionPriceId,
    },
    {
      key: 'enterprise',
      eyebrow: 'INSTITUTIONAL',
      name: 'Enterprise',
      priceDisplay: 'Custom',
      priceCaption: 'Annual contract',
      priceId: null,
    },
  ];

  // Comparison grid rows. Add rows here as features ship (carryover credits, the
  // deep-dive / MCP add-ons) — the grid layout leaves room to grow downward.
  readonly featureRows: ChatFeatureRow[] = [
    {
      label: 'Deep Research',
      values: { free: '3 / mo', pro: '15 / mo', team: '100 / mo pooled', enterprise: 'Unlimited' },
    },
    {
      label: 'Snapshot',
      values: { free: '15', pro: 'Unlimited', team: 'Unlimited', enterprise: 'Unlimited' },
    },
    {
      label: 'Users',
      values: { free: '1', pro: '1', team: 'Up to 5', enterprise: 'Custom' },
    },
  ];

  protected get productId(): string {
    return environment.chatSubscriptionProductId;
  }

  constructor(
    subscriptionsService: CustomerSubscriptionsService,
    authService: AuthService,
    notificationService: NotificationService,
    billingService: BillingService,
    private dialog: MatDialog,
    @Optional() private dialogRef?: MatDialogRef<ChatProductSubscriptionComponent>
  ) {
    super(subscriptionsService, authService, notificationService, billingService);
    this.isDialog = !!this.dialogRef;
  }

  ngOnInit(): void {
    super.initializeBaseSubscriptionLogic();
    this.isOrgAdmin$ = this.authService.$isOrgAdmin;
  }

  // Any time the subscription is loaded or changed, reflect the active tier.
  protected onSubscriptionLoaded(): void {
    this.currentPriceId = this.currentSubscription?.product?.price?.id ?? null;
  }

  // Free is the baseline: it's "current" whenever there's no paid subscription.
  isCurrentTier(tier: ChatTier): boolean {
    if (tier.key === 'free') {
      return !this.currentPriceId;
    }
    return !!tier.priceId && tier.priceId === this.currentPriceId;
  }

  // True when this tier maps to a subscription that's active but scheduled to
  // cancel at term end. Such a plan can't be "switched to" — it's already winding
  // down — so we surface the end date and disable its action.
  isTierCanceling(tier: ChatTier): boolean {
    return !!this.cancelingSubFor(tier);
  }

  tierEndsAt(tier: ChatTier): string | null {
    return this.cancelingSubFor(tier)?.endsAt ?? null;
  }

  private cancelingSubFor(tier: ChatTier) {
    if (!tier.priceId) {
      return undefined;
    }
    return this.allProductSubscriptions.find(
      (s) => s.product?.price?.id === tier.priceId && this.isCanceling(s)
    );
  }

  ctaLabel(tier: ChatTier): string {
    if (this.isCurrentTier(tier)) {
      return 'Current plan';
    }
    // A separate, still-active-but-canceling sub for this tier (dual-sub case).
    if (this.isTierCanceling(tier)) {
      return 'Ending soon';
    }
    if (tier.key === 'enterprise') {
      return 'Contact sales';
    }
    if (tier.key === 'free') {
      // Only reachable when on a paid plan (Free-as-current returns above).
      return 'Cancel plan';
    }
    return this.currentPriceId ? `Switch to ${tier.name}` : `Start ${tier.name}`;
  }

  actionDisabled(tier: ChatTier): boolean {
    return (
      this.isCurrentTier(tier) ||
      this.isTierCanceling(tier) ||
      this.isPollingForUpdate ||
      this.isLoadingSubscriptions
    );
  }

  onTierAction(tier: ChatTier): void {
    // Never let the user switch to a plan that's already canceling.
    if (this.isCurrentTier(tier) || this.isTierCanceling(tier)) {
      return;
    }

    switch (tier.key) {
      case 'enterprise':
        this.openIntercomForSales();
        return;
      case 'free':
        // Downgrade to Free = cancel the active paid subscription (via Stripe portal).
        this.cancelSubscription();
        return;
      default:
        if (!tier.priceId || tier.priceId.includes('PLACEHOLDER')) {
          this.notificationService.error(
            'This plan is not available yet. Please contact support.'
          );
          Log.e('ChatComponent: Missing Stripe price id for tier', tier.key);
          return;
        }
        if (this.currentSubscription?.id) {
          // In-place change on an existing subscription charges the card on file
          // immediately, so confirm upgrades first.
          this.confirmThenChangePlan(tier);
        } else {
          this.startCheckout(tier.priceId);
        }
    }
  }

  private currentTierKey(): ChatTierKey {
    if (!this.currentPriceId) {
      return 'free';
    }
    return this.tiers.find((t) => t.priceId === this.currentPriceId)?.key ?? 'free';
  }

  private isUpgrade(tier: ChatTier): boolean {
    return TIER_RANK[tier.key] > TIER_RANK[this.currentTierKey()];
  }

  // Upgrading in place charges the card on file immediately (BE side), so confirm
  // before proceeding. Downgrades change billing without an immediate charge and
  // skip the prompt.
  private confirmThenChangePlan(tier: ChatTier): void {
    if (!tier.priceId) {
      return;
    }
    if (!this.isUpgrade(tier)) {
      this.changePlan(tier.priceId);
      return;
    }
    this.dialog
      .open(PlanChangeConfirmDialogComponent, {
        panelClass: 'subscription-dialog',
        backdropClass: 'darker-dialog-backdrop',
        data: {
          title: `Upgrade to ${tier.name}`,
          points: [
            `Immediately charges your card the ${tier.name} price`,
            'Grants you the higher usage tiers and functionality',
          ],
          confirmLabel: `Upgrade to ${tier.name}`,
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed && tier.priceId) {
          this.changePlan(tier.priceId);
        }
      });
  }

  private startCheckout(priceId: string): void {
    const req: CreateCheckoutSessionRequest = { priceId };
    this.subscriptionsService
      .createCheckoutSession(this.authService.getSelectedOrgIdValue, req)
      .subscribe({
        next: (res) => super.handleStripeCheckoutSession(res.sessionUrl),
        error: (err) => super.handleBillingError(err, 'creating your subscription'),
      });
  }

  private changePlan(priceId: string): void {
    if (!this.currentSubscription?.id) {
      return;
    }
    this.isLoadingSubscriptions = true;
    const req: UpdateSubscriptionRequest = { priceId };
    this.subscriptionsService
      .updateSubscription(this.authService.getSelectedOrgIdValue, this.currentSubscription.id, req)
      .subscribe({
        next: () => {
          // In dialog mode, close immediately and let the service poll/refresh in
          // the background so the left-nav badge and gauge update on their own.
          if (this.isDialog) {
            this.dialogRef?.close({ success: true });
          }
          super.pollUntilConditionMet(
            () => this.currentSubscription?.product?.price?.id === priceId,
            'Subscription plan successfully updated!'
          );
        },
        error: (err) => super.handleBillingError(err, 'updating your subscription'),
      });
  }

  private openIntercomForSales(): void {
    if ((window as any).Intercom) {
      (window as any).Intercom(
        'showNewMessage',
        'I would like to inquire about the Workforce.ai Enterprise plan.'
      );
    } else {
      this.notificationService.error(
        'Please contact us at sales@workforce.ai for more information.'
      );
      Log.e('ChatComponent: Intercom not available.');
    }
  }
}
